import { z } from 'zod'

export const nameSchema: z.ZodString = z
	.string()
	.min(2, { message: 'Слишком короткое имя' })
	.max(20, { message: 'Слишком длинное имя' })

export const surnameSchema: z.ZodString = z
	.string()
	.min(2, { message: 'Слишком короткая фамилия' })
	.max(20, { message: 'Слишком длинная фамилия' })

const imgSchema: z.ZodOptional<z.ZodString> = z.string().optional()

export const birthdaySchema = z
	.string()
	.refine(
		value => {
			const datePattern = /^\d{2}\.\d{2}\.\d{4}$/
			if (!datePattern.test(value)) {
				return false
			}

			const [day, month, year] = value.split('.').map(Number)
			const date = new Date(year, month - 1, day)

			return (
				date.getFullYear() === year &&
				date.getMonth() + 1 === month &&
				date.getDate() === day
			)
		},
		{
			message: 'Неверный формат даты или дата не существует',
		}
	)
	.optional()

const userLevel = z.enum(['Новичок', 'Любитель', 'Профессионал', 'Спортсмен']).optional()

export const genderSchema = z.enum(['Мужской', 'Женский']).optional()

export const favoriteSportSchema = z.string().min(0).max(100).optional()
export const yearsExperienceSchema = z.string().optional()

const coinsAmountSchema = z.number().min(0).max(100_000).optional()

export const weightSchema = z
	.string()
	// число в формате 'abc' или 'ab', где a != 0
	.regex(/^(?!0\d)\d+(\.\d+)?$/, { message: 'Вес должен быть числом без ведущих нулей' })
	.min(2, { message: 'Очень маленький вес' })
	.max(5, { message: 'Очень большой вес' })
	.optional()

export const heightSchema = z
	.string()
	// число в формате 'abc' или 'ab', где a != 0
	.regex(/^(?!0\d)\d+(\.\d+)?$/, { message: 'Рост должен быть числом без ведущих нулей' })
	.min(2, { message: 'Вам нужно подрасти' })
	.max(3, { message: 'Вы слишком высокий' })
	.optional()

export const physiqueSchema = z.enum(['Эктоморф', 'Мезоморф', 'Эндоморф']).optional()

export const experienceSchema = z.enum(['Новичок', 'Средний уровень', 'Продвинутый']).optional()

export const activityLevelSchema = z
	.enum(['Сидячий образ жизни', 'Среднеактивный образ жизни', 'Высокоактивный образ жизни'])
	.optional()

export const healthInformationSchema = z.string().max(500).optional()

export const contraindicationsSchema = z.string().max(500).optional()

export const weekTrainingAmountSchema = z
	.string()
	//числа от 1 до 99
	.regex(/^([1-9]|[1-9][0-9])$/, { message: 'Выберите количество тренировок от 1 до 99' })
	.optional()

export const trainingsTargetsSchema = z
	.enum(['Снижение веса', 'Набор массы', 'Поддержание формы', 'Улучшение выносливости'])
	.optional()

export const favoriteTrainingTypeSchema = z
	.enum(['Кардио', 'Силовые', 'Функциональные тренировки', 'Растяжка'])
	.optional()

export const stressLevelSchema = z.enum(['Отсутствует', 'Низкий', 'Средний', 'Высокий']).optional()

export const desiredResultSchema = z
	.string()
	.regex(/^-?\d{1,3}$/)
	.refine(
		value => {
			const num = parseInt(value, 10)
			return num >= -200 && num <= 200
		},
		{
			message: 'Значение должно быть числом в диапазоне от -200 до +200',
		}
	)
	.optional()

export const fatnessLevelSchema = z
	.string()
	.regex(/^([1-9]|[1-9][0-9])$/, { message: 'Процент жира может быть от 1 до 99' })
	.optional()

export const dietSchema = z
	.string()
	.min(0)
	.max(500, { message: 'Сократите ваше сообщение о диете' })
	.optional()

export const caloriesAmountSchema = z
	.string()
	.min(0, { message: 'Введите валидное число' })
	.max(5, { message: 'Слишком большое число' })
	.optional()

export const UserInfoSchema = z.object({
	firstName: nameSchema,
	lastName: surnameSchema,
	img: imgSchema,
	birthday: birthdaySchema,
	level: userLevel,
	gender: genderSchema,
	coinsAmount: coinsAmountSchema,
	weight: weightSchema,
	height: heightSchema,
	physique: physiqueSchema,
	favoriteTrainingType: favoriteTrainingTypeSchema,
	experience: experienceSchema,
	activityLevel: activityLevelSchema,
	trainingsTargets: trainingsTargetsSchema,
	healthInformation: healthInformationSchema,
	contraindications: contraindicationsSchema,
	weekTrainingAmount: weekTrainingAmountSchema,
	desiredResult: desiredResultSchema,
	stressLevel: stressLevelSchema,
	fatnessLevel: fatnessLevelSchema,
	diet: dietSchema,
	caloriesAmount: caloriesAmountSchema,
})

export type UserInfo = z.infer<typeof UserInfoSchema>
export type UserGender = z.infer<typeof genderSchema>
export type UserPhysique = z.infer<typeof physiqueSchema>
export type UserExperience = z.infer<typeof experienceSchema>
export type UserActivity = z.infer<typeof activityLevelSchema>
export type UserHealth = z.infer<typeof healthInformationSchema>
export type UserContraindication = z.infer<typeof contraindicationsSchema>
export type TrainingsTargets = z.infer<typeof trainingsTargetsSchema>
export type FavoriteTrainingType = z.infer<typeof favoriteTrainingTypeSchema>
export type StressLevel = z.infer<typeof stressLevelSchema>
export type FatnessLevel = z.infer<typeof fatnessLevelSchema>
export type Diet = z.infer<typeof dietSchema>
export type CaloriesAmount = z.infer<typeof caloriesAmountSchema>

export const defaultUserInfo: Readonly<UserInfo> = {
	firstName: '',
	lastName: '',
	img: undefined,
	birthday: undefined,
	level: undefined,
	gender: undefined,
	coinsAmount: undefined,
	weight: undefined,
	height: undefined,
	physique: undefined,
	experience: undefined,
	activityLevel: undefined,
	healthInformation: undefined,
	contraindications: undefined,
	weekTrainingAmount: undefined,
	desiredResult: undefined,
}
